/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { 
  Search, 
  Server,
  ChevronRight,
  Loader2,
  X,
  CheckCircle,
  Wrench,
  Database,
  FileText,
  MessageSquare,
} from 'lucide-react';

// Services
import {
  // R10: Use centralized query keys for cache coordination
  MCP_QUERY_KEYS,
  listMcpServers,
  searchMcpServers,
  getMcpServer,
  getMcpServerDocs,
  verifyMcpServer,
  getMcpServerCategories,
} from '@/services/skillshop';
import type { McpServerEntry, McpServerCategory } from '@/services/skillshop';

// Shared components
import { getMcpServerStatusConfig } from '../shared/statusConfig';
import { InstallButton, InstallButtonCompact } from './InstallButton';

export function McpServersTab() {
  // R9: Persist filter state in URL for shareability and navigation survival
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read filters from URL with defaults
  const categoryFilter = searchParams.get('category') || 'all';
  const statusFilter = (searchParams.get('status') as 'all' | 'active' | 'deprecated' | 'unavailable') || 'all';
  const selectedServerId = searchParams.get('id');
  
  // Local state for search query (not persisted until search executed)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // R9: Helper to update URL params while preserving others
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === 'all' || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }, [setSearchParams]);

  // R10: Fetch MCP servers using centralized query key
  const { data: servers, isLoading: isLoadingServers } = useQuery({
    queryKey: MCP_QUERY_KEYS.list,
    queryFn: listMcpServers,
    staleTime: 5 * 60 * 1000,
  });

  // R10: Fetch categories using centralized query key
  const { data: categoriesData } = useQuery({
    queryKey: MCP_QUERY_KEYS.categories,
    queryFn: getMcpServerCategories,
    staleTime: 30 * 60 * 1000,
  });

  // Search servers
  const { data: searchResults, isLoading: isSearching, refetch: doSearch } = useQuery({
    queryKey: ['mcp-search', searchQuery, categoryFilter, statusFilter],
    queryFn: async () => {
      return searchMcpServers(
        searchQuery || undefined,
        {
          category: categoryFilter !== 'all' ? categoryFilter as McpServerCategory : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          limit: 50,
        }
      );
    },
    enabled: false, // Manual trigger
  });

  // Verify server mutation
  const verifyServerMutation = useMutation({
    mutationFn: (id: string) => verifyMcpServer(id),
    onSuccess: (result) => {
      if (result.available) {
        toast({
          title: 'Server Available',
          description: 'The MCP server is available and responding.',
        });
      } else {
        toast({
          title: 'Server Unavailable',
          description: result.message || 'The MCP server is not responding.',
          variant: 'destructive',
        });
      }
      // R10: Use centralized query key
      queryClient.invalidateQueries({ queryKey: MCP_QUERY_KEYS.all });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to verify server.',
        variant: 'destructive',
      });
    },
  });

  // R6: React Query for server docs - caches and handles loading states
  const { 
    data: serverDocs, 
    isLoading: isLoadingDocs,
  } = useQuery({
    queryKey: ['mcp-server-docs', selectedServerId],
    queryFn: () => getMcpServerDocs(selectedServerId!).catch(() => null),
    enabled: !!selectedServerId,
    staleTime: 5 * 60 * 1000, // Cache docs for 5 minutes
  });

  // Get selected server metadata from list
  const selectedServer = selectedServerId
    ? servers?.find(s => s.id === selectedServerId) ?? 
      searchResults?.results?.find(r => r.server.id === selectedServerId)?.server ?? 
      null
    : null;

  // R6 + R9: Select server by ID - React Query handles fetching, URL persists selection
  const selectServer = useCallback((server: McpServerEntry) => {
    updateParams({ id: server.id });
  }, [updateParams]);

  // R6 + R9: Close focus view - React Query keeps data cached
  const closeFocusView = useCallback(() => {
    updateParams({ id: null });
  }, [updateParams]);

  // R9: Handle search and persist query to URL
  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      updateParams({ q: searchQuery.trim() });
    }
    doSearch();
  }, [searchQuery, doSearch, updateParams]);

  // R9: Clear search and remove from URL
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    updateParams({ q: null });
    queryClient.removeQueries({ queryKey: ['mcp-search'] });
  }, [queryClient, updateParams]);

  // Get unique categories from servers
  const categories = categoriesData || [];

  // Determine which servers to display
  const displayServers = searchResults?.results?.map(r => r.server) || servers;

  // Filter servers
  const filteredServers = displayServers?.filter((s: McpServerEntry) => {
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="flex h-[calc(100vh-320px)]">
      {/* Servers List */}
      <div className={`flex flex-col ${selectedServerId ? 'w-1/2 border-r' : 'w-full'}`}>
        {/* Search and Filters */}
        <div className="p-4 border-b space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search MCP servers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} disabled={isSearching}>
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
          
          <div className="flex gap-2">
            {/* R9: Filters persisted to URL */}
            <Select value={categoryFilter} onValueChange={(v) => updateParams({ category: v })}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => updateParams({ status: v })}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="deprecated">Deprecated</SelectItem>
                <SelectItem value="unavailable">Unavailable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Server Cards */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoadingServers ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredServers?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Server className="h-12 w-12 mb-4 opacity-50" />
              <p>No MCP servers found</p>
              {searchQuery && (
                <Button variant="link" onClick={clearSearch}>Clear search</Button>
              )}
            </div>
          ) : (
            filteredServers?.map((server) => (
              <McpServerCard
                key={server.id}
                server={server}
                isSelected={selectedServerId === server.id}
                onClick={() => selectServer(server)}
              />
            ))
          )}
        </div>
      </div>

      {/* Focus View (Server Detail) - R6: React Query handles loading/caching */}
      {selectedServerId && selectedServer && (
        <McpServerFocusView
          server={selectedServer}
          docs={serverDocs ?? null}
          isLoadingDocs={isLoadingDocs}
          onClose={closeFocusView}
          onVerify={() => verifyServerMutation.mutate(selectedServer.id)}
          isVerifying={verifyServerMutation.isPending}
        />
      )}
    </div>
  );
}

// MCP Server Card Component
interface McpServerCardProps {
  server: McpServerEntry;
  isSelected: boolean;
  onClick: () => void;
}

function McpServerCard({ server, isSelected, onClick }: McpServerCardProps) {
  const config = getMcpServerStatusConfig(server.status);
  const StatusIcon = config.icon;

  return (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'border-primary bg-primary/5' : 'bg-slate-50 hover:border-gray-300'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Server className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h3 className="font-medium truncate">{server.name}</h3>
            <StatusIcon className={`h-4 w-4 ${config.className}`} />
          </div>
          {server.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {server.description}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="bg-gray-50">
              {server.category}
            </Badge>
            {server.version && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                v{server.version}
              </Badge>
            )}
            {/* Capability counts */}
            {server.capabilities && (
              <>
                {server.capabilities.tools?.length > 0 && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    <Wrench className="h-3 w-3 mr-1" />
                    {server.capabilities.tools.length} tools
                  </Badge>
                )}
                {server.capabilities.resources?.length > 0 && (
                  <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                    <Database className="h-3 w-3 mr-1" />
                    {server.capabilities.resources.length} resources
                  </Badge>
                )}
                {server.capabilities.prompts?.length > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    {server.capabilities.prompts.length} prompts
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
          <InstallButtonCompact
            type="mcp"
            itemId={server.id}
            itemName={server.name}
          />
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}

// MCP Server Focus View Component
interface McpServerFocusViewProps {
  server: McpServerEntry;
  docs: string | null;
  isLoadingDocs: boolean;
  onClose: () => void;
  onVerify: () => void;
  isVerifying: boolean;
}

function McpServerFocusView({ server, docs, isLoadingDocs, onClose, onVerify, isVerifying }: McpServerFocusViewProps) {
  const config = getMcpServerStatusConfig(server.status);
  const StatusIcon = config.icon;

  return (
    <div className="w-1/2 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Server className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold truncate">{server.name}</h2>
          </div>
          <p className="text-sm text-muted-foreground truncate">{server.source}</p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <InstallButton
            type="mcp"
            itemId={server.id}
            itemName={server.name}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={onVerify}
            disabled={isVerifying}
          >
            {isVerifying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-1" />
            )}
            Verify
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-6">
          {/* Status */}
          <div className={`flex items-center gap-2 p-3 rounded-lg ${config.bannerClassName}`}>
            <StatusIcon className="h-5 w-5" />
            <span className="font-medium">{config.label}</span>
            {server.lastVerified && (
              <span className="text-sm opacity-70">
                Last verified: {new Date(server.lastVerified).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Description */}
          {server.description && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
              <p className="text-sm">{server.description}</p>
            </div>
          )}

          {/* Capabilities */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Capabilities</h4>
            <div className="space-y-4">
              {/* Tools */}
              {server.capabilities?.tools?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="h-4 w-4 text-purple-500" />
                    <span className="text-sm font-medium">Tools ({server.capabilities.tools.length})</span>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {server.capabilities.tools.map((tool, idx) => (
                      <span key={idx} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Resources */}
              {server.capabilities?.resources?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-cyan-500" />
                    <span className="text-sm font-medium">Resources ({server.capabilities.resources.length})</span>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {server.capabilities.resources.map((resource, idx) => (
                      <span key={idx} className="text-xs bg-cyan-50 text-cyan-700 px-2 py-1 rounded">
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Prompts */}
              {server.capabilities?.prompts?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium">Prompts ({server.capabilities.prompts.length})</span>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {server.capabilities.prompts.map((prompt, idx) => (
                      <span key={idx} className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
                        {prompt}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Authentication */}
          {server.authentication && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Authentication</h4>
              <dl className="text-sm space-y-2">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-20">Type:</dt>
                  <dd>
                    <Badge variant="outline">{server.authentication.type}</Badge>
                  </dd>
                </div>
                {server.authentication.envVar && (
                  <div className="flex gap-2">
                    <dt className="text-muted-foreground w-20">Env Var:</dt>
                    <dd className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                      {server.authentication.envVar}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {/* Related Skills */}
          {server.relatedSkills && server.relatedSkills.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Related Skills</h4>
              <div className="flex gap-1 flex-wrap">
                {server.relatedSkills.map((skill, idx) => (
                  <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Documentation */}
          {isLoadingDocs ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : docs ? (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Documentation</h4>
              <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                {docs}
              </div>
            </div>
          ) : null}

          {/* Metadata */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Details</h4>
            <dl className="text-sm space-y-2">
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-20">Category:</dt>
                <dd>{server.category}</dd>
              </div>
              {server.version && (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground w-20">Version:</dt>
                  <dd>{server.version}</dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-20">Source:</dt>
                <dd className="text-xs font-mono truncate">{server.source}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
